/**
 * Session API (F1). Mirrors PRD §7 REST surface:
 *   POST /sessions · GET /sessions (?q=&lang=&from=&to=) · PATCH /sessions/{id}
 *
 * Field names match the `sessions` table (PRD §6): source_lang_mode (auto|manual),
 * target_lang (single per session), tts_enabled, and per-kind provider ids.
 */

import { http } from '@/shared/api/http';

export type SourceLangMode = 'auto' | 'manual';
export type SessionStatus = 'live' | 'ended' | 'error';

/** A session row as returned by the backend. */
export interface Session {
  id: string;
  name: string;
  source_lang_mode: SourceLangMode;
  source_lang: string | null;
  target_lang: string;
  tts_enabled: boolean;
  provider_stt: string;
  provider_translate: string;
  provider_tts: string | null;
  status: SessionStatus;
  created_at: string;
  ended_at: string | null;
}

/** Body for POST /sessions. */
export interface CreateSessionInput {
  name: string;
  source_lang_mode: SourceLangMode;
  /** Required when source_lang_mode === 'manual', otherwise null. */
  source_lang: string | null;
  target_lang: string;
  tts_enabled: boolean;
  provider_stt: string;
  provider_translate: string;
  provider_tts: string | null;
}

/** Optional filters for GET /sessions. */
export interface ListSessionsQuery {
  q?: string;
  lang?: string;
  from?: string;
  to?: string;
}

function buildQuery(query: ListSessionsQuery): string {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.lang) params.set('lang', query.lang);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const sessionApi = {
  create(input: CreateSessionInput): Promise<Session> {
    return http.post<Session>('/sessions', { json: input });
  },

  list(query: ListSessionsQuery = {}): Promise<Session[]> {
    return http.get<Session[]>(`/sessions${buildQuery(query)}`);
  },

  /** Rename a session (PATCH /sessions/{id}). */
  rename(id: string, name: string): Promise<Session> {
    return http.patch<Session>(`/sessions/${encodeURIComponent(id)}`, {
      json: { name },
    });
  },
};
